import { PushNotifications, Token, ActionPerformed } from '@capacitor/push-notifications';
import MainService from './main-service';
import FormatService from './format-service';
import { NODE_ENV_PRODUCTION } from '../constants/system/node-envs';

export default class PushNotificationService {
  private static PUSH_NOTIFICATION_PERMISSION_STATUS_GRANTED: string = 'granted';
  private static PUSH_NOTIFICATION_PERMISSION_STATUS_PROMPT: string = 'prompt';
  private static PUSH_NOTIFICATION_DURATION_DEFAULT: number = 15000;
  private static DEFAULT_PHOTO_PATH: string = '/images/push-icon.png';

  public static async checkIfPushNotificationsAllowed(): Promise<boolean> {
    const isMobileApp: boolean = !!MainService.IS_MOBILE_APP;

    if (isMobileApp) {
      let mobilePushNotificationResult: PushNotificationPermission = await PushNotifications.checkPermissions();

      if (mobilePushNotificationResult.receive === this.PUSH_NOTIFICATION_PERMISSION_STATUS_PROMPT) {
        mobilePushNotificationResult = await PushNotifications.requestPermissions();
      }

      return mobilePushNotificationResult.receive === this.PUSH_NOTIFICATION_PERMISSION_STATUS_GRANTED;
    } else {
      return false;
    }
  }

  public static async enablePushNotifications(
    onRegisterPushNotifications: (token: string) => void,
    onPushNotificationActionPerformed: (pushNotificationData: Record<string, StructureItem>) => void,
  ): Promise<void> {
    const isMobileApp: boolean = !!MainService.IS_MOBILE_APP;

    if (isMobileApp) {
      await PushNotifications.register();

      await PushNotifications.addListener('registration', (token: Token): void => {
        const pushNotificationToken: string = token.value;

        if (onRegisterPushNotifications && pushNotificationToken) {
          onRegisterPushNotifications(pushNotificationToken);
        }
      });

      await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action: ActionPerformed): void => {
          if (onPushNotificationActionPerformed && action.notification && action.notification.data) {
            onPushNotificationActionPerformed(action.notification.data);
          }
        },
      );
    }
  }

  public static checkIfWebPushNotificationsEnabled(): boolean {
    const isMobileApp: boolean = !!MainService.IS_MOBILE_APP;
    const isProductionMode: boolean = MainService.NODE_ENV === NODE_ENV_PRODUCTION;

    return isMobileApp ? false : (isProductionMode && typeof Notification !== 'undefined');
  }

  public static checkIfWebPushNotificationsAllowed(): boolean {
    return this.checkIfWebPushNotificationsEnabled()
      && Notification.permission === this.PUSH_NOTIFICATION_PERMISSION_STATUS_GRANTED;
  }

  public static async allowWebPushNotifications(): Promise<void> {
    if (this.checkIfWebPushNotificationsEnabled()) {
      await Notification.requestPermission();
    }
  };

  public static addNewWebPushNotification(
    subtitle: string,
    text: string,
    links: Array<FormatLinkItem>,
    photo: string,
  ): void {
    const isMobileApp: boolean = !!MainService.IS_MOBILE_APP;
    const isProductionMode: boolean = MainService.NODE_ENV === NODE_ENV_PRODUCTION;
    const platformTitle: string | undefined = MainService.PLATFORM_TITLE;

    if (isMobileApp || !isProductionMode || !platformTitle || typeof Notification === 'undefined') {
      return;
    }

    const notification: Notification = new Notification(platformTitle, {
      body: FormatService.formatLinkedText(
        text, links, false, false, true, (link: FormatLinkItem, linkText: string): string => linkText,
      ).toString(),
      data: subtitle,
      icon: photo || this.DEFAULT_PHOTO_PATH,
    });

    setTimeout(notification.close.bind(notification), this.PUSH_NOTIFICATION_DURATION_DEFAULT);
  }
}
